import React, { useCallback, useMemo, useState } from 'react'

import clsx from 'clsx'

import Dialog from 'components/Dialog'
import Filters from 'components/Filters'
import Grid, { Container } from 'components/Grid'
import OneTapScript from 'components/OneTapScript'
import ProductInfoCard from 'components/ProductInfoCard'
import SoftwareApplicationLD from 'components/SoftwareApplicationLD'
import Typography from 'components/Typography'
import { useAppContext } from 'context/AppContext'
import { currentYear } from 'helpers/currentYear'
import { filterAlternatives, updateFacetCounts } from 'helpers/dataTransformers/alternativesBxApi'
import sortAlternatives from 'helpers/sortAlternatives'
import { useUser } from 'hooks'
import useViewport from 'hooks/useViewport'

import AlternativeCard from './components/AlternativeCard'
import FreeAlternatives from './components/FreeAlternatives'
import PopularComparisons from './components/PopularComparisons'

import classes from './AlternativesLayout.module.scss'

const parseFiltersFromUrl = href => {
  try {
    const url = new URL(href, 'https://localhost')
    const filters = {}
    for (const [key, value] of url.searchParams) {
      filters[key] = value
    }
    return filters
  } catch {
    return {}
  }
}

const rebuildFacets = (baseFacets, allAlternatives, activeFilters, basePath) => {
  const filtered = sortAlternatives(filterAlternatives(allAlternatives, activeFilters))
  const updatedFacets = updateFacetCounts(baseFacets, filtered)

  // Update selected state and hrefs based on active filters
  const FACET_TO_PARAM = {
    feature_tags_facet: 'tag_slug',
    pricingcategories_facet: 'pricingcategories_slug',
    oses_facet: 'oses_slug',
    rating_stars_ge: 'rating_stars_ge',
    typecustomers_facet: 'typecustomers_slug',
    integrated_with_facet: 'integrated_with_slug',
    community_company_industry: 'community_company_industry',
    community_company_size: 'community_company_size',
  }

  const result = {}
  for (const [facetKey, items] of Object.entries(updatedFacets)) {
    const paramName = FACET_TO_PARAM[facetKey]
    if (!paramName) continue
    result[facetKey] = items.map(item => {
      // "all" value (used in rating_stars_ge) means no filter active
      if (item.value === 'all') {
        const isSelected = !activeFilters[paramName]
        const newFilters = { ...activeFilters }
        delete newFilters[paramName]
        const qs = Object.entries(newFilters)
          .map(([k, v]) => `${k}=${encodeURIComponent(v)}`)
          .join('&')
        const href = qs ? `${basePath}?${qs}` : basePath
        return { ...item, selected: isSelected, href }
      }

      const isSelected = String(activeFilters[paramName]) === String(item.value)
      // Build href: if selected, remove filter; if not, add filter
      const newFilters = { ...activeFilters }
      if (isSelected) {
        delete newFilters[paramName]
      } else {
        newFilters[paramName] = item.value
      }
      const qs = Object.entries(newFilters)
        .map(([k, v]) => `${k}=${encodeURIComponent(v)}`)
        .join('&')
      const href = qs ? `${basePath}?${qs}` : basePath

      return { ...item, selected: isSelected, href }
    })
  }
  return { facets: result, alternatives: filtered }
}

const AlternativesLayout = ({
  listing,
  facets: initialFacets,
  queryParams,
  alternatives: initialAlternatives,
  popularComparisons,
}) => {
  const { isCypressTakingSnapshots, head, header } = useAppContext()
  const { up, down } = useViewport()
  const [user] = useUser()

  const basePath = queryParams.base_path || ''
  const [activeFilters, setActiveFilters] = useState({})

  const { alternatives, facets } = useMemo(
    () => rebuildFacets(initialFacets, initialAlternatives, activeFilters, basePath),
    [initialFacets, initialAlternatives, activeFilters, basePath]
  )

  const handleFilterUpdate = useCallback(href => {
    setActiveFilters(parseFiltersFromUrl(href))
  }, [])

  const [dialogOpen, setDialogOpen] = useState(false)

  const hasFreeAlternatives =
    !!listing.summary.top.length &&
    !facets.pricingcategories_facet.some(facet => facet.value !== 'free' && facet.selected)

  return (
    <div className="AlternativesLayout">
      <OneTapScript user={user} />
      <ProductInfoCard
        isCypressTakingSnapshots={isCypressTakingSnapshots}
        listingInfoData={listing}
        head={head}
        header={header}
        buyerIntentMainCtaSourceLocation="alternatives/product-info-card:main-cta"
      />
      <SoftwareApplicationLD listing={listing} />

      <Container fixedWidth className={classes.pageContentContainer}>
        <Typography
          component="h1"
          variant="heading-lg"
          className={clsx(classes.mainHeading, {
            [classes.headingExtraMargin]: !hasFreeAlternatives,
          })}
        >
          {`${listing.name} Alternatives & Competitors (${currentYear})`}
        </Typography>
        {hasFreeAlternatives && down('sm') && <FreeAlternatives top={listing.summary.top} />}
        <Grid container spacing={3} className={classes.content}>
          {up('md') && (
            <Grid item md={4} lg={3} className={classes.filtersContainer}>
              {hasFreeAlternatives && <FreeAlternatives top={listing.summary.top} />}
              <div className={classes.fullHeight}>
                <Filters
                  facets={facets}
                  rootUrl={basePath}
                  variant="permanent"
                  topSpacing={100}
                  softSPA
                  softUpdate={handleFilterUpdate}
                />
              </div>
            </Grid>
          )}
          <Grid item xs={12} md={8} lg={9} className={classes.relativeResults}>
            <div className={classes.relativeResults}>
              {down('sm') && (
                <Filters
                  facets={facets}
                  rootUrl={basePath}
                  variant="temporary"
                  stickyFilterPosition={classes.stickyFilterPositionNoContent}
                  softSPA
                  softUpdate={handleFilterUpdate}
                />
              )}

              <div className={classes.alternativesCards}>
                {alternatives.map((alternative, i) => (
                  <div className={classes.alternativeCardWithAnchor} key={`alternativeCard-${alternative.id}`}>
                    <div className={classes.anchor} id={alternative.slug} />
                    <AlternativeCard alternative={alternative} listing={listing} index={i} />
                  </div>
                ))}
              </div>

              {!!popularComparisons.length && (
                <section id="popular-comparisons" className="ContentItem">
                  <PopularComparisons listing={listing} comparisons={popularComparisons} />
                </section>
              )}
            </div>
          </Grid>
        </Grid>
      </Container>
      <Dialog
        contentClassName={classes.dialog}
        title="How did we choose these products?"
        isOpen={dialogOpen}
        onClose={() => setDialogOpen(false)}
      >
        Alternative products featured in this article were identified in user reviews as the other products buyers
        considered at the same time as the title product. In this article, we feature the products with the highest
        number of &quot;other products considered&quot; mentions. To be included in this list, alternative products had
        to provide the same core features as the title product.
        <br />
        <br />
        Analysis performed in June 2022. In order to present the most up-to-date information, the product information
        below shows the latest real-time ratings, which may differ from the ratings values at the time this
        report&apos;s analysis was conducted, since new reviews may have been left in the meantime.
        <br />
        <br />
        We select and rank products based on an objective methodology developed by our research team. While some vendors
        may pay us when they receive web traffic or leads, this has no influence on our methodology.
      </Dialog>
    </div>
  )
}

export default AlternativesLayout
